import Select from "react-select";
import Top from "./Top";
import Right from "./Right";
import { useEffect, useState } from "react";
import { server } from "../../server";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { getUserFromToken } from "../../function";
import { Link, useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";

function CreateEvent() {
  const navigate = useNavigate();
  const [remainingEvents, setRemainingEvents] = useState({
    regular: 0,
    basic: 0,
  });

  const events = [
    { value: "wedding", label: "Wedding" },
    { value: "engagement", label: "Engagement" },
    { value: "pre-wedding", label: "Pre-Wedding" },
    { value: "maternity", label: "Maternity" },
    { value: "baby-shoot", label: "Baby Shoot" },
    { value: "events", label: "Events" },
    { value: "expo", label: "Expo" },
    { value: "conference", label: "Conference" },
    { value: "music-festival", label: "Music Festival" },
    { value: "marathon", label: "Marathon" },
    { value: "corporate-event", label: "Corporate Event" },
    { value: "fashion-show", label: "Fashion Show" },
    { value: "team-outing", label: "Team Outing" },
    { value: "dealers-meet", label: "Dealers Meet" },
    { value: "hackathons", label: "Hackathons" },
    { value: "family-day", label: "Family Day" },
    { value: "annual-summit", label: "Annual Summit" },
    { value: "corporate-sports", label: "Corporate Sports" },
    { value: "other", label: "Other" },
  ];

  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    eventType: "",
    description: "",
    pin: "",
    phones: "",
    eventPhoto: null,
    category: "regular",
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const eventId = uuidv4();
    const user = getUserFromToken();
    const userId = user?._id;

    try {
      let eventPhotoUrl = "";

      if (formData.eventPhoto) {
        const fileName = `${Date.now()}-${formData.eventPhoto.name}`;

        // 1. Get presigned URL
        const presignRes = await fetch(
          `${server}/PresignedUrl/GeneratePresignedUrl`,
          {
            method: "POST",
            headers: {
              Authorization: token,
            },
            body: JSON.stringify({
              folder: "events",
              fileNames: [fileName],
            }),
          }
        );

        const raw = await presignRes.json();
        const urls = raw.urls || JSON.parse(raw.body).urls;
        const uploadDetails = urls[0];

        // 2️⃣ Compress image before upload (target < 1MB)
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        let compressedFile = formData.eventPhoto;

        try {
          const compressedBlob = await imageCompression(
            formData.eventPhoto,
            options
          );
          // fallback if slightly over 1MB
          if (compressedBlob.size > 1024 * 1024) {
            const moreCompressed = await imageCompression(formData.eventPhoto, {
              ...options,
              maxSizeMB: 0.8,
            });
            compressedFile = new File(
              [moreCompressed],
              formData.eventPhoto.name,
              {
                type: formData.eventPhoto.type,
              }
            );
          } else {
            compressedFile = new File(
              [compressedBlob],
              formData.eventPhoto.name,
              {
                type: formData.eventPhoto.type,
              }
            );
          }
        } catch (err) {
          console.error("Compression failed:", err);
        }

        // 3️⃣ Upload the compressed file
        await fetch(uploadDetails.signedUrl, {
          method: "PUT",
          headers: { "Content-Type": uploadDetails.contentType },
          body: compressedFile,
        });

        eventPhotoUrl = uploadDetails.key;
      }

      // 4. Submit event data
      const payload = {
        userObjectId: userId,
        id: eventId,
        name: formData.name,
        eventPhoto: eventPhotoUrl,
        eventType: formData.eventType,
        description: formData.description,
        pin: formData.pin,
        phones: formData.phones
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean),
        category: formData.category,
      };

      const response = await fetch(`${server}/Event/CreateEvent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success("Event Created");
        navigate("/all-events");
      } else {
        const errorMsg =
          data?.message || data?.error || data?.msg || "Failed to Create Event";
        toast.error(errorMsg);
      }
    } catch (err) {
      toast.error("Something Went Wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = getUserFromToken();
    if (!user?._id) return;

    const fetchMembership = async () => {
      try {
        const res = await fetch(
          `${server}/GetMembershipPlanAndHistory?userObjectId=${user._id}`,
          {
            headers: { Authorization: token },
          }
        );

        const data = await res.json();

        const plans = data?.plans || {};

        setRemainingEvents({
          regular: plans?.regular?.remainingEvents ?? 0,
          basic: plans?.basic?.remainingEvents ?? 0,
        });
      } catch (err) {
        console.error("Failed to load membership data", err);
      }
    };

    fetchMembership();
  }, [token]);

  return (
    <>
      <main>
        <Top />

        <div className="container">
          <div className="row g-3">
            <div className="col-lg-9 col-12 py-2">
              <section class="position-relative z-1">
                <div className="pb-3 d-flex flex-column flex-md-row gap-0 align-items-md-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <Link
                      to="/all-events"
                      className="action-button-md bg-white-transparent rounded-circle"
                    >
                      <i className="bi bi-arrow-left fs-4 text-dark"></i>
                    </Link>
                    <div>
                      <h1 className="mb-0 h4 text-light lh-1 fw-medium ms-3">
                        Create Event
                      </h1>
                      <nav aria-label="breadcrumb" className="m-0 ms-3">
                        <ol className="breadcrumb">
                          <li className="breadcrumb-item">
                            <span>Home</span>
                          </li>
                          <li
                            className="breadcrumb-item active"
                            aria-current="page"
                          >
                            New Event
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>
                </div>
              </section>

              <section class="position-relative z-1">
                <div>
                  <form onSubmit={handleSubmit}>
                    <div className="row g-2">
                      <div className="col-lg-4 col-md-6">
                        <label className="label-text text-light mb-1 ms-3">
                          Category <span className="text-danger">*</span>
                        </label>

                        <div className="d-flex gap-1 w-100">
                          <div
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                category: "regular",
                              }))
                            }
                            className={`p-2 px-3 w-100 rounded-3 border ${
                              formData.category === "regular"
                                ? "border-primary bg-primary text-white"
                                : "border-light bg-light"
                            }`}
                            style={{ cursor: "pointer" }}
                          >
                            <input
                              type="radio"
                              name="category"
                              value="regular"
                              checked={formData.category === "regular"}
                              className="me-2"
                              readOnly
                            />
                            Regular ({remainingEvents.regular})
                          </div>

                          <div
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                category: "basic",
                              }))
                            }
                            className={`p-2 px-3 w-100 rounded-3 border ${
                              formData.category === "basic"
                                ? "border-primary bg-primary text-white"
                                : "border-light bg-light"
                            }`}
                            style={{ cursor: "pointer" }}
                          >
                            <input
                              type="radio"
                              name="category"
                              value="basic"
                              checked={formData.category === "basic"}
                              className="me-2"
                              readOnly
                            />
                            Basic ({remainingEvents.basic})
                          </div>
                        </div>

                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Select Regular or Basic category &mdash;{" "}
                          <Link to="/pricing">Buy Now</Link>
                        </p>
                      </div>

                      <div className="col-lg-4 col-md-6">
                        <label className="label-text text-light mb-1 ms-3">
                          Event Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          class="form-control ps-3 normal-text bg-light border border-light rounded-3"
                          placeholder="Enter Event Name"
                        />
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Name your event.
                        </p>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <label className="label-text text-light mb-1 ms-3">
                          Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          class="form-control ps-3 normal-text bg-light border border-light rounded-3"
                          placeholder="Enter Date"
                        />
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          When did the event occur
                        </p>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <label className="label-text text-light mb-1 ms-3">
                          Type of Event <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={events}
                          placeholder="Select Event"
                          className="bg-light rounded-4"
                          isClearable
                          value={
                            events.find(
                              (e) => e.value === formData.eventType
                            ) || null
                          }
                          onChange={(opt) =>
                            setFormData((prev) => ({
                              ...prev,
                              eventType: opt?.value || "",
                            }))
                          }
                        />
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Select the type of event
                        </p>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <label className="label-text text-light mb-1 ms-3">
                          Event Photo
                          {formData.category === "regular" && (
                            <span className="text-danger"> *</span>
                          )}
                        </label>
                        <input
                          type="file"
                          name="eventPhoto"
                          onChange={handleChange}
                          class="form-control ps-3 normal-text bg-light border border-light rounded-3"
                        />
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Event photo for banner.
                        </p>
                      </div>
                      <div className="col-12">
                        <label className="label-text text-light mb-1 ms-3">
                          Event Description{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          type="text"
                          class="form-control ps-3 normal-text bg-light border border-light rounded-3"
                          rows="3"
                          placeholder="Enter Event Description"
                        />
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Enter few lines about your Event
                        </p>
                      </div>
                      <div className="col-12 my-lg-1 mt-5">
                        <h5 className="m-0 lh-1 text-light mt-4">
                          <i className="bi bi-lock"></i> Privacy
                        </h5>
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Privacy of sharing images of this event
                        </p>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <label className="label-text text-light mb-1 ms-3">
                          Guest access PIN{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="pin"
                          value={formData.pin}
                          onChange={handleChange}
                          maxLength={4}
                          class="form-control ps-3 normal-text bg-light border border-light rounded-3"
                          placeholder="Enter PIN"
                        />
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Please provide 4 digit PIN
                        </p>
                      </div>
                      <div className="col-lg-8 col-md-12">
                        <label className="label-text text-light mb-1 ms-3">
                          Event Phone No.{" "}
                          <span className="text-danger">
                            <span className="smtext">(Comma Seperated)</span> *
                          </span>
                        </label>
                        <textarea
                          type="text"
                          name="phones"
                          value={formData.phones}
                          onChange={handleChange}
                          class="form-control ps-3 normal-text bg-light border border-light rounded-3"
                          rows="2"
                          placeholder="Enter Phone No."
                        />
                        <p className="smtext m-0 ms-3 me-5 mt-1 lh-1 light-text-white">
                          Enter Phone No.(s) you want to grant direct access
                        </p>
                      </div>

                      <div className="col-lg-3 col-md-5 mt-lg-4 mt-6">
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-dark-primary border-0 px-4 rounded-3 fs-6 fw-medium"
                        >
                          {loading ? (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            ></div>
                          ) : (
                            <>
                              <i className="bi bi-cloud me-1"></i> Create Event
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </section>
            </div>

            <div className="col-lg-3 col-12">
              <Right />
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default CreateEvent;
